import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { DataTableColumn, DataTableSortDirection } from 'src/app/shared/ui/data-table/data-table.component';
import { ManagerService, FileService, LocalService, Errors, ProfileService, formatDate } from '../../../../core';
import { TranslateService } from '@ngx-translate/core';
import { managerApi } from '../../../../config';
import { ActivityFilterValue } from '../activity-filter-form/activity-filter-form.component';

@Component({
  selector: 'app-taxable-redemption',
  templateUrl: './taxable-redemption.component.html',
  styleUrls: ['./taxable-redemption.component.scss']
})
export class TaxableRedemptionComponent implements OnInit, AfterViewInit {
  @ViewChild('redemptionDateTemplate') redemptionDateTemplate!: TemplateRef<any>;
  @ViewChild('redemptionAmountTemplate') redemptionAmountTemplate!: TemplateRef<any>;

  userProfile: any;
  usersList: any[] = [];
  minDate: Date = new Date(2020, 7, 31);
  maxDate: Date = new Date();
  taxableRedemptionData: any;
  taxableRedemptionPage = 0;
  taxableRedemptionSortBy = '';
  taxableRedemptionSortDir: DataTableSortDirection = '';
  taxableRedemptionLoading = false;
  taxableRedemptionColumns: DataTableColumn[] = [];
  errors: Errors = { errors: {} };
  pageSize = 5;

  private lastFilter: ActivityFilterValue = { selectedUsers: [], startDate: null, endDate: null };

  constructor(
    private managerService: ManagerService,
    private fileService: FileService,
    private profileService: ProfileService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.userProfile = LocalService.get('userProfile');
    this.getUsersUnderManager();
  }

  ngAfterViewInit(): void {
    this.taxableRedemptionColumns = [
      { field: 'sellerName', header: this.translate.instant('accountDetails.sellerName'), align: 'left', sortable: true },
      { field: 'sellerCcoid', header: this.translate.instant('accountDetails.sellerCcoid'), align: 'left' },
      { field: 'redemptionDate', header: this.translate.instant('accountDetails.redemptionDate'), align: 'left', sortable: true, cellTemplate: this.redemptionDateTemplate },
      { field: 'redemptionAmount', header: this.translate.instant('accountDetails.redemptionAmount'), align: 'right', cellTemplate: this.redemptionAmountTemplate },
      { field: 'calendarQuarterPeriod', header: this.translate.instant('accountDetails.quarterPeriod'), align: 'left' }
    ];
  }

  onFilterSearch(filter: ActivityFilterValue): void {
    this.lastFilter = filter;
    this.taxableRedemptionPage = 0;
    this.taxableRedemptionSortBy = '';
    this.taxableRedemptionSortDir = '';
    this.taxableRedemptionLoading = true;
    this.fetchTaxableRedemptionData(0);
  }

  onFilterReset(): void {
    this.lastFilter = { selectedUsers: [], startDate: null, endDate: null };
    this.taxableRedemptionPage = 0;
    this.taxableRedemptionSortBy = '';
    this.taxableRedemptionSortDir = '';
    this.taxableRedemptionData = null;
  }

  onFilterExport(filter: ActivityFilterValue): void {
    this.lastFilter = filter;
    const startDate = formatDate(filter.startDate, 'YYYY-MM-DD HH:mm:ss');
    const endDate = formatDate(filter.endDate, 'YYYY-MM-DD HH:mm:ss');
    const downloadName = this.translate.instant('accountDetails.downloadTaxStatement');
    const fileName = downloadName + '_' + this.userProfile.company.id + '.xlsx';
    const url = managerApi.pointAccountRedemptionTaxStatement +
      '?companyId=' + this.userProfile.company.id +
      '&startDate=' + startDate +
      '&endDate=' + endDate +
      '&excel=true';
    this.fileService.excelDownloadFile(url, fileName);
  }

  onTaxableRedemptionPageChange(newPage: number): void {
    this.taxableRedemptionPage = newPage - 1;
    this.fetchTaxableRedemptionData(newPage - 1);
  }

  onTaxableRedemptionSort(event: { sortBy: string; sortDir: DataTableSortDirection }): void {
    this.taxableRedemptionSortBy = event.sortBy;
    this.taxableRedemptionSortDir = event.sortDir;
    this.taxableRedemptionPage = 0;
    this.fetchTaxableRedemptionData(0);
  }

  splitData(value: any): string {
    return parseFloat(value).toLocaleString();
  }

  getUsersUnderManager(): void {
    this.profileService.companyAllUsers(this.userProfile.company.id).subscribe(
      (data: any[]) => {
        if (data) {
          this.usersList = data.reduce((users: any[], item: any) => {
            if (users.find((obj: any) => obj.id === item.id) === undefined) {
              users.push({ id: item.id, fullName: item.firstName + ' ' + item.lastName });
            }
            return users;
          }, []).sort((a: any, b: any) => a.fullName.localeCompare(b.fullName));
        }
      },
      (err: any) => {
        this.errors = err;
      }
    );
  }

  private fetchTaxableRedemptionData(pageNumber = 0, pageSize = 5): void {
    const startDate = formatDate(this.lastFilter.startDate, 'YYYY-MM-DD HH:mm:ss');
    const endDate = formatDate(this.lastFilter.endDate, 'YYYY-MM-DD HH:mm:ss');

    const params: any = {
      companyId: this.userProfile.company.id,
      startDate: startDate,
      endDate: endDate,
      page: pageNumber,
      size: pageSize,
    };

    if (this.lastFilter.selectedUsers && this.lastFilter.selectedUsers.length > 0) {
      params.userIds = this.lastFilter.selectedUsers.map((user: any) => user.id).join(',');
    }

    if (this.taxableRedemptionSortBy) {
      params.sortBy = this.taxableRedemptionSortBy;
      params.sortDir = this.taxableRedemptionSortDir.toUpperCase();
    }

    this.taxableRedemptionLoading = true;
    this.managerService.getRedemptionTaxStatement(params).subscribe(
      (data: any) => {
        this.taxableRedemptionData = data;
        this.taxableRedemptionLoading = false;
      },
      (err: any) => {
        this.errors = err;
        this.taxableRedemptionLoading = false;
      }
    );
  }
}
